import { stableJsonSha256 } from "../design-ontology-binding/stable-json-hash"
import type { CompiledApplicationComposition } from "../composition/types"
import type { LibraryCompositionArtifact } from "../library-composition/types"

export interface SourceBoundInteractionSubject {
  designOntologyReleaseRef: string
  applicationSemanticHash: string
  designBindingHash: string
  renderContractHash: string
}

export async function computeSourceBoundInteractionSubject(input: {
  library: LibraryCompositionArtifact
  design: CompiledApplicationComposition
}): Promise<SourceBoundInteractionSubject> {
  return {
    designOntologyReleaseRef: input.design.designReleaseRef,
    applicationSemanticHash: await stableJsonSha256(
      applicationSemanticProjection(input.library),
    ),
    designBindingHash: await stableJsonSha256(
      designBindingProjection(input.design),
    ),
    renderContractHash: await stableJsonSha256(input.design),
  }
}

function applicationSemanticProjection(library: LibraryCompositionArtifact) {
  const { recipeRef: _recipeRef, semanticNodes, ...plan } = library.plan

  return {
    scopeManifest: library.scopeManifest,
    resolvedBindings: library.resolvedBindings,
    runtime: {
      ...library.runtime,
      semanticActions: library.runtime.semanticActions.map(
        ({ declaration, ...action }) => ({
          ...action,
          declaration: {
            role: declaration.role,
            capabilityRef: declaration.capabilityRef,
          },
        }),
      ),
    },
    plan: {
      ...plan,
      semanticNodes: semanticNodes.map(
        ({ regionRef: _regionRef, action, ...node }) => ({
          ...node,
          ...(action
            ? {
                action: {
                  role: action.role,
                  capabilityRef: action.capabilityRef,
                },
              }
            : {}),
        }),
      ),
    },
  }
}

function designBindingProjection(design: CompiledApplicationComposition) {
  return {
    designOntologyReleaseRef: design.designReleaseRef,
    recipeRef: design.recipeRef,
    nodes: design.nodes.map((node) => ({
      nodeId: node.nodeId,
      componentRef: node.componentRef,
      rendererRef: node.rendererRef,
      regionRef: node.regionRef,
      tokenRefs: node.tokenRefs,
      action: node.action
        ? {
            role: node.action.role,
            slotRef: node.action.slotRef,
            tokenRef: node.action.tokenRef,
            exclusiveGroup: node.action.exclusiveGroup,
          }
        : null,
    })),
    ...(design.generationTrace.projectValidationAuthority
      ? {
          projectValidationAuthority: structuredClone(
            design.generationTrace.projectValidationAuthority,
          ),
        }
      : {}),
  }
}
